/**
 * RecentlySeenSection — Shows patients with recent documents (Today / This Week).
 *
 * Derives data from the compact patient summary written when documents change.
 * No document subcollection or audit-log query is required.
 * Supports user-configurable sort and persisted collapse state.
 */
import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { setPatientPinPreference } from "../../utils/patientDirectoryService";
import { useAuth } from "../../utils/authContext";
import DashboardSection from "./DashboardSection";
import PatientRow from "./PatientRow";
import { sortDashboardPatients } from "./dashboardSortUtil";
import { filterRecentlySeenPatients } from "./recentlySeenDate";

const SECTION_TITLE = "Recently Seen";

const RecentlySeenSection = ({ allPatients, dashboardPrefs }) => {
    const [range, setRange] = useState("today"); // "today" | "week"
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { getSectionPrefs, setSectionSort, setSectionCollapsed } = dashboardPrefs;
    const sectionPrefs = getSectionPrefs(SECTION_TITLE);

    // lastSeenAt is a real activity timestamp from a completed note/upload. It
    // is intentionally separate from the date-only clinical session fields.
    const recentPatients = useMemo(() => {
        return filterRecentlySeenPatients(allPatients, range);
    }, [allPatients, range]);

    const sortedPatients = useMemo(
        () => sortDashboardPatients(recentPatients, sectionPrefs.sortField, sectionPrefs.sortDirection),
        [recentPatients, sectionPrefs.sortField, sectionPrefs.sortDirection]
    );

    const handleNavigate = useCallback((id) => {
        navigate("/patient", { state: { patientId: id, from: "/dashboard" } });
    }, [navigate]);

    const handleRecord = useCallback((id) => {
        navigate("/patient", { state: { patientId: id, pendingAction: "record", from: "/dashboard" } });
    }, [navigate]);

    const handlePinToggle = useCallback(async (patient) => {
        if (patient.isShared || !currentUser?.uid) return;
        try {
            await setPatientPinPreference(currentUser.uid, patient.id, !patient.pin);
        } catch (err) {
            console.error("[RecentlySeenSection] Error toggling pin:", err);
        }
    }, [currentUser?.uid]);

    const handleSort = useCallback((field, direction) => {
        setSectionSort(SECTION_TITLE, field, direction);
    }, [setSectionSort]);

    const handleToggleCollapse = useCallback((collapsed) => {
        setSectionCollapsed(SECTION_TITLE, collapsed);
    }, [setSectionCollapsed]);

    const toggleActions = (
        <div className="cdv-toggle-group" onClick={(e) => e.stopPropagation()}>
            <button
                className={`cdv-toggle-btn${range === "today" ? " active" : ""}`}
                onClick={() => setRange("today")}
            >
                Today
            </button>
            <button
                className={`cdv-toggle-btn${range === "week" ? " active" : ""}`}
                onClick={() => setRange("week")}
            >
                This Week
            </button>
        </div>
    );

    return (
        <DashboardSection
            title={SECTION_TITLE}
            count={recentPatients.length}
            collapsed={sectionPrefs.collapsed}
            onToggleCollapse={handleToggleCollapse}
            actions={toggleActions}
            sortField={sectionPrefs.sortField}
            sortDirection={sectionPrefs.sortDirection}
            onSort={handleSort}
            columnHeader={true}
        >
            {sortedPatients.length === 0 ? (
                <p className="cdv-section-hint">
                    {range === "today" ? "No patients seen today." : "No patients seen this week."}
                </p>
            ) : (
                sortedPatients.map((patient) => (
                    <PatientRow
                        key={patient.id}
                        patient={patient}
                        onNavigate={handleNavigate}
                        onRecord={handleRecord}
                        showPin={!patient.isShared}
                        onPinToggle={handlePinToggle}
                    />
                ))
            )}
        </DashboardSection>
    );
};

export default RecentlySeenSection;
